import { useEffect, useState } from "react";
import type { InstanceDetail } from "@keydials/api-client";
import { Dial } from "@keydials/ui";
import { instanceToDialModel } from "@keydials/dial-workspace";
import { api, useAuth } from "../auth/AuthContext";
import { KdLogo } from "./KdMark";
import { PinLogin } from "./PinLogin";

/** How often the wall display re-reads its dial. Long enough to be nearly free on a tablet left on all
 * day, short enough that a status set on someone else's machine appears while you're still stood there. */
const REFRESH_MS = 20_000;

/**
 * What a shared device shows once it has gone idle (KEY-268): the dial itself, its name, and the Key Dials
 * mark — a wall display that stays useful at a glance while nobody is signed in.
 *
 * The face comes from a lock-ticket-only endpoint that strips members, attribute values and every "who
 * changed it" before it leaves the server, so the screen carries status and nothing personal. It is drawn
 * with the same `instanceToDialModel` the workspace renders through, so the locked dial IS the dial —
 * not a second renderer that drifts from it the first time a colour rule changes.
 *
 * It refreshes on a timer rather than over the realtime hub: the hub would have to be opened up to a
 * capability-free lock ticket, and a wall display is not worth that. A dial that repaints within twenty
 * seconds reads as live.
 *
 * Touching anywhere opens the unlock flow. The whole surface is the target because the audience is a carer
 * mid-round, quite possibly wearing gloves.
 */
export function LockedScreen() {
  const { lock, logout } = useAuth();
  const [waking, setWaking] = useState(false);
  const [face, setFace] = useState<InstanceDetail | null>(null);
  // Null until the first read resolves either way, so the screen doesn't flash the fallback mark.
  const [loaded, setLoaded] = useState(false);

  const ticket = lock?.ticket ?? null;
  const instanceId = lock?.instanceId ?? null;

  useEffect(() => {
    if (!ticket || !instanceId) return;
    let cancelled = false;

    const read = async () => {
      try {
        const next = await api.lockFace(ticket, instanceId);
        if (!cancelled) setFace(next);
      } catch {
        // A 404 means auto-lock was switched off underneath this device; anything else is a blip. Either
        // way the dial comes off the wall rather than being left showing something stale and wrong.
        if (!cancelled) setFace(null);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };

    void read();
    const timer = window.setInterval(() => void read(), REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [ticket, instanceId]);

  if (!lock) {
    return null;
  }

  if (waking) {
    return <PinLogin onCancel={() => setWaking(false)} />;
  }

  const model = face ? instanceToDialModel(face) : null;

  return (
    <button
      type="button"
      onClick={() => setWaking(true)}
      aria-label="Touch to unlock"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 18,
        padding: 32,
        border: "none",
        background: "var(--bg)",
        cursor: "pointer",
        font: "inherit",
      }}
    >
      <KdLogo height={40} />

      {model && face ? (
        <>
          <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text)", textAlign: "center" }}>
            {face.name}
          </div>
          {/* Presentational only: no selection, no click targets — every touch wakes the device instead. */}
          <div style={{ pointerEvents: "none", display: "flex", justifyContent: "center" }}>
            <Dial
              model={model}
              size={Math.min(440, Math.round(Math.min(window.innerWidth, window.innerHeight) * 0.56))}
              centreImageUrl={lock.imageUrl}
              ariaLabel={`${face.name} — locked`}
            />
          </div>
        </>
      ) : loaded && lock.imageUrl ? (
        // No face to show (auto-lock withdrawn, or the read failed): fall back to the dial's own logo,
        // which is what the lock screen showed before it displayed the dial at all.
        <img
          src={lock.imageUrl}
          alt=""
          style={{ maxWidth: "min(360px, 55vw)", maxHeight: "42vh", objectFit: "contain" }}
        />
      ) : null}

      <div style={{ fontSize: 17, fontWeight: 600, color: "var(--text-muted)", textAlign: "center" }}>
        Touch the screen to sign in
      </div>

      {/* The way out for someone who isn't on this dial at all — a full sign-out, then the normal login. */}
      <span
        role="link"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          void logout();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.stopPropagation();
            void logout();
          }
        }}
        style={{
          position: "absolute",
          bottom: 24,
          fontSize: 13.5,
          color: "var(--text-faint)",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        Sign in as someone else
      </span>
    </button>
  );
}
